import React, { Component } from "react"
import PropTypes from "prop-types"
import Autosuggest from "react-autosuggest"

import i18n from "@cirrus/legacy/app-common/alt/framework/i18n.js"
import {
  getAst,
  getFilterNodeByPosition,
} from "@cirrus/legacy/features/core/shared/utils/smartSearch/selectors/index"

import styles from "./filterAutosuggest.less"

const getFilterNode = (value, startPos, endPos, supportedFilterKeys) => {
  let searchAst = getAst(value, true, supportedFilterKeys)
  return getFilterNodeByPosition(searchAst, startPos, endPos)
}

class FilterAutosuggest extends Component {
  static propTypes = {
    filterSuggestions: PropTypes.object,
    inputProps: PropTypes.object,
    renderInputComponent: PropTypes.func,
  }

  state = {
    suggestions: [],
    suggestionsToSetOnUpdate: null,
    isFocused: false,
  }

  startPos = null
  endPos = null
  getSuggestionValueCalled = false
  lastNode = null
  lastValueBeforeSuggestion = null

  _getSupportedFilterKeys() {
    return this.props.filterSuggestions == null ? [] : Object.keys(this.props.filterSuggestions)
  }

  _setSearchRef = (ref) => {
    this._searchRef = ref
  }

  _getSuggestions(key, value) {
    const inputValue = value.trim().toLowerCase()
    const inputLength = inputValue.length

    if (inputLength < 2) {
      return []
    }

    let rawSuggestions = this.props.filterSuggestions[key]

    if (rawSuggestions == null) {
      return []
    }

    return rawSuggestions
      .filter((rawSuggestions) => rawSuggestions.indexOf('"') === -1)
      .filter((rawSuggestion) => rawSuggestion.toLowerCase().slice(0, inputLength) === inputValue)
      .map((rawSuggestion) => `${key}: ${rawSuggestion}`)
  }

  _onSuggestionsFetchRequested(method) {
    this.startPos = this._searchRef.selectionStart
    this.endPos = this._searchRef.selectionEnd

    let node = getFilterNode(
      this._searchRef.value,
      this.startPos,
      this.endPos,
      this._getSupportedFilterKeys(),
    )

    if (node == null) {
      this.lastValueBeforeSuggestion = this._searchRef.value

      this.setState({
        suggestions: [],
      })
    } else if (
      (method !== "left" && method !== "right") ||
      this.lastNode == null ||
      this.lastNode.startIndex !== node.startIndex
    ) {
      if (
        this.lastNode == null ||
        this.lastNode.startIndex !== node.startIndex ||
        this.lastNode.endIndex !== node.endIndex
      ) {
        this.lastValueBeforeSuggestion = this._searchRef.value
      }

      if (this.lastNode != null && this.lastNode.startIndex !== node.startIndex) {
        // If suggestions changed from one not empty array to another, then old selected suggestion index can be applied to new suggestions array.
        // Resetting suggestions to empty array will reset selected suggestion index.
        this.setState({
          suggestions: [],
          suggestionsToSetOnUpdate: this._getSuggestions(node.key, node.value.value),
        })
      } else {
        this.setState({
          suggestions: this._getSuggestions(node.key, node.value.value),
        })
      }
    }

    this.lastNode = node
  }

  _updateCursorPosition(newValue) {
    if (!this.state.isFocused) {
      return
    }

    let node = getFilterNode(
      this._searchRef.value,
      this.startPos,
      this.endPos,
      this._getSupportedFilterKeys(),
    )
    let newNode = getFilterNode(
      newValue,
      node.startIndex,
      node.startIndex,
      this._getSupportedFilterKeys(),
    )

    this.startPos = newNode.endIndex
    this.endPos = newNode.endIndex
  }

  componentDidUpdate() {
    if (this.state.suggestionsToSetOnUpdate != null) {
      this.setState({
        suggestionsToSetOnUpdate: null,
        suggestions: this.state.suggestionsToSetOnUpdate,
      })
    }

    if (!this.state.isFocused) {
      return
    }

    if (
      (this.startPos != null && this._searchRef.selectionStart !== this.startPos) ||
      (this.endPos != null && this._searchRef.selectionEnd !== this.endPos)
    ) {
      this._searchRef.selectionStart = this.startPos
      this._searchRef.selectionEnd = this.endPos
    }
  }

  render() {
    let { inputProps, renderInputComponent } = this.props

    let { suggestions } = this.state

    return (
      <div>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={({ reason }) => {
            if (reason === "input-focused") {
              return
            }

            this._onSuggestionsFetchRequested()
          }}
          onSuggestionsClearRequested={() => {
            this.setState({
              suggestions: [],
            })
          }}
          getSuggestionValue={(suggestion) => {
            let search = this._searchRef.value
            let node = getFilterNode(
              this._searchRef.value,
              this.startPos,
              this.endPos,
              this._getSupportedFilterKeys(),
            )
            let suggestionValue = suggestion.replace(`${node.key}:`, "").trim()

            this.getSuggestionValueCalled = true

            return `${search.slice(0, node.startIndex)}"${suggestionValue}"${search.slice(
              node.endIndex,
            )}`
          }}
          renderSuggestion={(suggestion) => <div>{suggestion}</div>}
          renderInputComponent={(inputProps) =>
            renderInputComponent({
              ...inputProps,
              ref: undefined,
              setRef: (ref) => {
                inputProps.ref(ref)
                inputProps.setRef(ref)
              },
            })
          }
          inputProps={{
            ...inputProps,
            setRef: (ref) => {
              this._setSearchRef(ref)
              inputProps.setRef && inputProps.setRef(ref)
            },
            onChange: (e, { newValue, method }) => {
              if (method === "up" || method === "down") {
                if (!this.getSuggestionValueCalled) {
                  this._updateCursorPosition(this.lastValueBeforeSuggestion)
                  inputProps.onChange(e, this.lastValueBeforeSuggestion)
                  return
                }

                this.getSuggestionValueCalled = false
              }
              if (method !== "type") {
                this._updateCursorPosition(newValue)
              }
              inputProps.onChange(e, newValue)
            },
            onKeyUp: (e) => {
              if (e.keyCode === 37 || e.keyCode === 39) {
                this._onSuggestionsFetchRequested(e.keyCode === 37 ? "left" : "right")
              }

              inputProps.onKeyUp && inputProps.onKeyUp(e)
            },
            onClick: (e) => {
              this._onSuggestionsFetchRequested()
            },
            onFocus: (e) => {
              this.setState({
                isFocused: true,
              })

              inputProps.onFocus && inputProps.onFocus(e)
            },
            onBlur: (e) => {
              this.setState({
                isFocused: false,
              })

              this.startPos = null
              this.endPos = null

              inputProps.onBlur && inputProps.onBlur(e)
            },
          }}
          theme={{
            container: styles.container,
            containerOpen: styles.containerOpen,
            suggestionsContainer: styles.suggestionsContainer,
            suggestion: styles.suggestion,
            suggestionHighlighted: styles.suggestionHighlighted,
            suggestionsList: styles.suggestionsList,
          }}
        />
      </div>
    )
  }
}

export default FilterAutosuggest
